import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSession } from "../lib/session";
import { listCourses, createCompetition, enableSwindle } from "../lib/api";
import { Field, Notice, Empty, PageHead, Loading } from "../components/ui";
import Back from "../components/Back";

/**
 * Setting up a competition.
 *
 * What you create is a competition. A normal society day is a competition
 * with a single round, so there's only one concept to learn.
 *
 * How many rounds count is asked after the rounds exist, not before —
 * otherwise you can choose "best 3" when you've only added two.
 */

const METRICS = [
  { k: "nett", label: "Nett", blurb: "Gross minus handicap. The usual society order." },
  { k: "stableford", label: "Stableford", blurb: "Points per hole. Pick-ups allowed." },
  { k: "gross", label: "Gross", blurb: "Scratch. No handicap applied." },
];

const newRound = () => ({ played_on: "", course_id: "", tees: [] });

export default function CompetitionNew() {
  const { society } = useSession();
  const nav = useNavigate();

  const [courses, setCourses] = useState(null);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [swindle, setSwindle] = useState(false);
  const [freeze, setFreeze] = useState(true);
  const [buyIn, setBuyIn] = useState(10);
  const [error, setError] = useState("");

  const [name, setName] = useState("");
  const [primaryMetric, setPrimaryMetric] = useState("nett");
  const [allowance, setAllowance] = useState(95);
  const [requiresHoleOut, setHoleOut] = useState(true);
  const [rounds, setRounds] = useState([newRound()]);
  const [aggregation, setAggregation] = useState("total");
  const [countingRounds, setCounting] = useState(3);

  useEffect(() => {
    listCourses(society.id).then(setCourses).catch((e) => setError(e.message));
  }, [society.id]);

  if (!courses) return <Loading />;

  if (courses.length === 0) {
    return (
      <>
        <Back to="/competitions">All competitions</Back>
        <PageHead title="New competition" />
        <Empty title="Add a course first"
               action={<button className="btn btn-primary" onClick={() => nav("/courses")}>
                 Go to courses
               </button>}>
          A competition needs somewhere to play. Add a course with its tees,
          par and stroke index, then come back.
        </Empty>
      </>
    );
  }

  const effectiveCounting = Math.min(countingRounds, rounds.length);
  const steps = ["Competition", "Rounds", "Review"];

  const setRound = (i, patch) =>
    setRounds(rounds.map((r, k) => (k === i ? { ...r, ...patch } : r)));

  function pickCourse(i, courseId) {
    const c = courses.find((x) => x.id === courseId);
    // default to every tee set the course has, marked for who plays them
    const tees = (c?.tee_sets ?? []).map((t) => ({
      tee_set_id: t.id,
      default_for: t.gender === "any" ? "" : t.gender,
      _name: t.name, _gender: t.gender }));
    setRound(i, { course_id: courseId, tees });
  }

  const roundsValid = rounds.every((r) => r.played_on && r.course_id && r.tees.length > 0);
  const canNext = step === 0 ? name.trim().length > 1 : step === 1 ? roundsValid : true;

  async function submit() {
    setError(""); setBusy(true);
    try {
      const comp = await createCompetition(society.id, {
        name: name.trim(),
        primaryMetric,
        allowance: primaryMetric === "gross" ? 0 : allowance,
        aggregation: rounds.length === 1 ? "single" : aggregation,
        countingRounds: aggregation === "best_n" ? effectiveCounting : null,
        requiresHoleOut: primaryMetric === "stableford" ? false : requiresHoleOut,
        freezeHandicaps: freeze,
      rounds: rounds.map((r) => ({
          played_on: r.played_on,
          course_id: r.course_id,
          tees: r.tees.map(({ tee_set_id, default_for }) => ({ tee_set_id, default_for })) })) });

      // after the rounds exist, so prizes can be named after them
      if (swindle) await enableSwindle(comp.id, buyIn);
      // Nobody is entered automatically — a four-round order of merit
      // rarely has the whole society in it, and un-entering people is more
      // work than entering them. Straight to choosing who's playing.
      nav(swindle ? `/c/${comp.id}/swindle/settings`
                  : `/competitions/${comp.id}/field`);
    } catch (err) {
      setError(err.message ?? String(err));
      setBusy(false);
    }
  }

  return (
    <>
      <Back to="/competitions">All competitions</Back>
      <PageHead title="New competition" sub={`Step ${step + 1} of ${steps.length} · ${steps[step]}`} />
      <Notice kind="error">{error}</Notice>

      {step === 0 && (
        <div className="surface" style={{ padding: "1.1rem" }}>
          <Field label="Name" hint="What your members will recognise it by.">
            <input className="input" autoFocus value={name}
                   onChange={(e) => setName(e.target.value)}
                   placeholder="The Club Championship" />
          </Field>

          <Field label="What decides it"
                 hint="All three are always worked out. This one orders the overall table; the others are prize categories.">
            <div className="stack" style={{ gap: ".4rem" }}>
              {METRICS.map((m) => (
                <button type="button" key={m.k} onClick={() => setPrimaryMetric(m.k)}
                        style={{
                          textAlign: "left", padding: ".7rem .85rem", cursor: "pointer",
                          borderRadius: "var(--r)", background: "none",
                          border: `${primaryMetric === m.k ? 2 : 1}px solid ${primaryMetric === m.k ? "var(--red)" : "var(--line)"}` }}>
                  <strong style={{ display: "block", fontSize: ".9375rem" }}>{m.label}</strong>
                  <span className="muted" style={{ fontSize: ".75rem" }}>{m.blurb}</span>
                </button>
              ))}
            </div>
          </Field>

          {primaryMetric !== "gross" && (
            <Field label="Handicap allowance"
                   hint="95% is the WHS recommendation for an individual competition. 100% for a social day.">
              <div style={{ display: "flex", gap: ".3rem" }}>
                {[85, 90, 95, 100].map((a) => (
                  <button type="button" key={a} onClick={() => setAllowance(a)}
                          style={{
                            flex: 1, padding: ".6rem 0", cursor: "pointer", fontWeight: 600,
                            borderRadius: "var(--r)",
                            border: `1px solid ${allowance === a ? "var(--ink)" : "var(--line)"}`,
                            background: allowance === a ? "var(--ink)" : "none",
                            color: allowance === a ? "#fff" : "var(--dim)" }}>{a}%</button>
                ))}
              </div>
            </Field>
          )}

          {primaryMetric !== "stableford" && (
            <Field label="Must players hole out?"
                   hint="Medal rules. If someone picks up, their card posts no nett or gross score — but their Stableford points still stand.">
              <div style={{ display: "flex", gap: ".3rem" }}>
                {[[true, "Yes — medal rules"], [false, "No — pick-ups allowed"]].map(([v, l]) => (
                  <button type="button" key={String(v)} onClick={() => setHoleOut(v)}
                          style={{
                            flex: 1, padding: ".6rem .4rem", cursor: "pointer", fontWeight: 600,
                            fontSize: ".8125rem", borderRadius: "var(--r)",
                            border: `1px solid ${requiresHoleOut === v ? "var(--ink)" : "var(--line)"}`,
                            background: requiresHoleOut === v ? "var(--ink)" : "none",
                            color: requiresHoleOut === v ? "#fff" : "var(--dim)" }}>{l}</button>
                ))}
              </div>
            </Field>
          )}
        </div>
      )}

      {step === 1 && (
        <>
          {rounds.map((r, i) => {
            const course = courses.find((c) => c.id === r.course_id);
            return (
              <div className="surface" key={i} style={{ padding: "1.1rem", marginBottom: ".8rem" }}>
                <div className="row" style={{ marginBottom: ".7rem" }}>
                  <strong style={{ textTransform: "uppercase" }}>
                    Round {i + 1}
                  </strong>
                  <span className="spacer" />
                  {rounds.length > 1 && (
                    <button type="button" className="btn-danger"
                            onClick={() => setRounds(rounds.filter((_, k) => k !== i))}>
                      Remove
                    </button>
                  )}
                </div>

                <div className="grid2">
                  <Field label="Date">
                    <input className="input" type="date" value={r.played_on}
                           onChange={(e) => setRound(i, { played_on: e.target.value })} />
                  </Field>
                  <Field label="Course">
                    <select className="select" value={r.course_id}
                            onChange={(e) => pickCourse(i, e.target.value)}>
                      <option value="">Choose…</option>
                      {courses.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </Field>
                </div>

                {course && (
                  <Field label="Tees in play"
                         hint="Untick any your field won't use. Course handicap keeps nett scores comparable across different tees.">
                    <div style={{ display: "flex", flexWrap: "wrap", gap: ".35rem" }}>
                      {(course.tee_sets ?? []).map((t) => {
                        const on = r.tees.some((x) => x.tee_set_id === t.id);
                        return (
                          <button type="button" key={t.id}
                                  onClick={() => setRound(i, {
                                    tees: on
                                      ? r.tees.filter((x) => x.tee_set_id !== t.id)
                                      : [...r.tees, {
                                          tee_set_id: t.id,
                                          default_for: t.gender === "any" ? "" : t.gender,
                                          _name: t.name, _gender: t.gender }] })}
                                  style={{
                                    padding: ".4rem .65rem", cursor: "pointer", fontSize: ".8125rem",
                                    fontWeight: 600, borderRadius: "var(--r)",
                                    border: `1px solid ${on ? "var(--ink)" : "var(--line)"}`,
                                    background: on ? "var(--ink)" : "none",
                                    color: on ? "#fff" : "var(--dim)" }}>
                            {t.name} · par {t.par} · {t.slope_rating}
                          </button>
                        );
                      })}
                    </div>
                  </Field>
                )}
              </div>
            );
          })}

          <button type="button" className="btn btn-ghost btn-block"
                  onClick={() => setRounds([...rounds, newRound()])}>
            Add round {rounds.length + 1}
          </button>

          {rounds.length > 1 && (
            <div className="surface" style={{ padding: "1.1rem", marginTop: ".8rem" }}>
              <Field label="How many rounds count?">
                <div style={{ display: "flex", gap: ".3rem", flexWrap: "wrap" }}>
                  <button type="button" onClick={() => setAggregation("total")}
                          style={{
                            flex: 1, minWidth: "8rem", padding: ".6rem .4rem", cursor: "pointer",
                            fontWeight: 600, fontSize: ".8125rem", borderRadius: "var(--r)",
                            border: `1px solid ${aggregation === "total" ? "var(--ink)" : "var(--line)"}`,
                            background: aggregation === "total" ? "var(--ink)" : "none",
                            color: aggregation === "total" ? "#fff" : "var(--dim)" }}>All {rounds.length}</button>
                  {Array.from({ length: rounds.length - 1 }, (_, k) => k + 1)
                    .filter((n) => n >= rounds.length - 3)
                    .map((n) => (
                      <button type="button" key={n}
                              onClick={() => { setAggregation("best_n"); setCounting(n); }}
                              style={{
                                flex: 1, minWidth: "5rem", padding: ".6rem .4rem", cursor: "pointer",
                                fontWeight: 600, fontSize: ".8125rem", borderRadius: "var(--r)",
                                border: `1px solid ${aggregation === "best_n" && effectiveCounting === n ? "var(--ink)" : "var(--line)"}`,
                                background: aggregation === "best_n" && effectiveCounting === n ? "var(--ink)" : "none",
                                color: aggregation === "best_n" && effectiveCounting === n ? "#fff" : "var(--dim)" }}>Best {n}</button>
                    ))}
                </div>
                <small>
                  {aggregation === "total"
                    ? "Every round counts. Anyone who misses one can't be ranked against a full set of scores."
                    : `Each player's worst ${rounds.length - effectiveCounting} drop${rounds.length - effectiveCounting === 1 ? "s" : ""}. They need ${effectiveCounting} completed rounds to place.`}
                </small>
              </Field>
            </div>
          )}
        </>
      )}

      {step === 2 && (
        <div className="surface" style={{ padding: "1.1rem" }}>
          {[
            ["Competition", name],
            ["Decided on", METRICS.find((m) => m.k === primaryMetric).label],
            ["Allowance", primaryMetric === "gross" ? "—" : `${allowance}%`],
            ["Hole out required", primaryMetric === "stableford" ? "No (Stableford)" : requiresHoleOut ? "Yes" : "No"],
            ["Rounds", rounds.length === 1 ? "One round"
              : aggregation === "total" ? `All ${rounds.length} count`
              : `Best ${effectiveCounting} of ${rounds.length}`],
          ].map(([k, v]) => (
            <div className="row" key={k}
                 style={{ padding: ".5rem 0", borderBottom: "1px solid var(--line)" }}>
              <span className="muted" style={{ fontSize: ".8125rem" }}>{k}</span>
              <span className="spacer" />
              <strong style={{ fontSize: ".875rem" }}>{v}</strong>
            </div>
          ))}

          {rounds.map((r, i) => (
            <div className="row" key={i}
                 style={{ padding: ".5rem 0", borderBottom: "1px solid var(--line)" }}>
              <span className="muted" style={{ fontSize: ".8125rem" }}>Round {i + 1}</span>
              <span className="spacer" />
              <span style={{ textAlign: "right", fontSize: ".8125rem" }}>
                <strong>{courses.find((c) => c.id === r.course_id)?.name}</strong><br />
                <span className="muted">
                  {r.played_on} · {r.tees.map((t) => t._name).join(", ")}
                </span>
              </span>
            </div>
          ))}

          <div className="surface" style={{ padding: "1rem 1.1rem", marginBottom: "1.1rem" }}>
            <span className="eyebrow">Handicaps</span>
            <div className="choices" style={{ margin: ".5rem 0 0" }}>
              {[[true, "Frozen at entry",
                 "The index a player enters with applies to every round. An order of merit can't be changed by a cut mid-season."],
                [false, "Whatever they're off",
                 "Each round uses their index when it opens. Right for a season of separate days."]].map(([v, t, d]) => (
                <label key={String(v)} className={`choice${freeze === v ? " on" : ""}`}>
                  <input type="radio" name="freeze" checked={freeze === v}
                         onChange={() => setFreeze(v)} />
                  <span>
                    <strong>{t}</strong>
                    <small>{d}</small>
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className="surface" style={{ padding: "1rem 1.1rem", marginBottom: "1.1rem" }}>
            <label className="row" style={{ cursor: "pointer", alignItems: "flex-start" }}>
              <input type="checkbox" checked={swindle}
                     onChange={(e) => setSwindle(e.target.checked)}
                     style={{ marginTop: ".2rem" }} />
              <span>
                <strong style={{ fontSize: ".9375rem" }}>Run a swindle alongside it</strong>
                <span className="muted" style={{ display: "block", fontSize: ".75rem",
                                                 lineHeight: 1.6, marginTop: ".15rem" }}>
                  The side bets — best front nine, most birdies, best nett on
                  the short holes. You'll pick which ones your society plays
                  next, and what each is worth.
                </span>
              </span>
            </label>
            {swindle && (
              <div style={{ marginTop: ".9rem", maxWidth: "12rem" }}>
                <Field label="Buy-in per player">
                  <input className="input" inputMode="decimal" value={buyIn}
                         onChange={(e) => setBuyIn(e.target.value)} />
                </Field>
              </div>
            )}
          </div>

          <p className="muted" style={{ fontSize: ".75rem", marginTop: ".9rem", lineHeight: 1.6 }}>
            Nobody is entered yet — you'll pick who's playing next. A player's
            handicap index is frozen when they enter, so a change later doesn't
            rewrite rounds they've already played. Anyone without an index
            can't be entered: add one on the members page
            afterwards.
          </p>
        </div>
      )}

      <div className="row" style={{ marginTop: "1.1rem" }}>
        {step > 0 && (
          <button className="btn btn-ghost" onClick={() => setStep(step - 1)}>Back</button>
        )}
        <span className="spacer" />
        {step < 2 ? (
          <button className="btn btn-primary" disabled={!canNext} onClick={() => setStep(step + 1)}>
            Continue
          </button>
        ) : (
          <button className="btn btn-primary" disabled={busy} onClick={submit}>
            {busy ? "Creating…" : "Create competition"}
          </button>
        )}
      </div>
    </>
  );
}
